import { useDispatch, useSelector } from 'react-redux';
import { selectTranslations } from '../../../App/i18n/i18nSlice';
import { RootState } from '../../../App/store';
import React, { useEffect, useState } from 'react';
import SessionService from '../../../App/SessionService';
import { RoleType, StorageKey } from '../../../App/enums';
import Button from '../../Shared/Primitive/Button';
import SearchInput from '../../Shared/Primitive/SearchInput';
import HTTPService from '../../../App/HTTPService';
import { headers } from '../../../App/Axios';
import { setModalData } from '../../Shared/SharedSlices/ModalSlice';
import { exportEventRequest, getEventsRequest } from '../RoomServiceRequests/EventServiceRequest';
import { setRoomEventsData } from '../RoomSlices/RoomEventsSlice';
import { RoomEventItem } from '../../../Types/RoomEvent';
import RoomEventListItem from '../RoomEvents/RoomEventItem';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TutorialBubble from '../../Shared/Components/TutorialBubble';

const RoomEvents: React.FC = () => {
    const roomEventsState = useSelector((state: RootState) => state.roomEventsSlice)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const [searchParams, setSearchParams] = useSearchParams()

    const dispatch = useDispatch()
    const [isOrgAdmin, setIsOrgAdmin] = React.useState(false)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    
    const orgId = SessionService.getItem(StorageKey.organizationId)
    const [createButtonDisabled, setCreateButtonDisabled] = React.useState(true)
    const translation = useSelector(selectTranslations)
    const [loading, setLoading] = useState(false)
    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [intialLoadDone, setInitialLoadDone] = useState(false)
    const [loadingFiles, setLoadingFiles] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        if (roomDataState.id > 0) {
            setLoading(true)
            dispatch(setRoomEventsData({ ...roomEventsState, selectedItem: {} as RoomEventItem }))
            const requestParams: [number, number, string] = [roomDataState.id, pageNum, roomEventsState.filter.query];
            if (role === RoleType.ORGANIZATION) {
                requestParams.push(Number(orgId!));
            }
            getEventsRequest(...requestParams)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setRoomEventsData({ ...roomEventsState, items: response.data.results }))
                        setHasNextPage(Boolean(response.data.next))
                        setInitialLoadDone(true)
                    }
                    setLoading(false)
                })
                .catch(error => {
                    console.log(error)
                })
        }
    }, [roomDataState.id])

    useEffect(() => {
        if (searchParams.get('event') && intialLoadDone) {
            const eventId = parseInt(searchParams.get('event')!)
            const selectedEvent = roomEventsState.items.find((event: RoomEventItem) => event.id === eventId)
            if (selectedEvent) {
                dispatch(setRoomEventsData({ ...roomEventsState, selectedItem: selectedEvent }))
                dispatch(setModalData({ open: true, roomEventModal: true }))
            }else {
                navigate("/not-found")
            }
        }
    }, [roomEventsState.items])

    useEffect(() => {
        if (loadingFiles || roomDataState.id === undefined) return
        const handler = setTimeout(() => {
            setLoadingFiles(true)
            setPageNum(1)
            const requestParams: [number, number, string, number?] = [roomDataState.id, pageNum, roomEventsState.filter.query];
            if (role === RoleType.ORGANIZATION) {
                requestParams.push(orgId!);
            }
            getEventsRequest(...requestParams)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setRoomEventsData({ ...roomEventsState, items: response.data.results }))
                        setHasNextPage(Boolean(response.data.next))
                    }
                    setLoadingFiles(false)
                })
                .catch(error => {
                    console.log(error)
                })
        }, 300)

        return () => {
            clearTimeout(handler)
        }
    }, [roomEventsState.filter])

    useEffect(() => {
        if (Number(roomDataState.organization?.id)) {
            HTTPService.get(`/role-in-org/${roomDataState.organization?.id}/`, headers).then(response => {
                if (response.status === 200) {
                    setIsOrgAdmin(response.data.is_admin)
                }
            })
        }
    }, [roomDataState.organization?.id])

    useEffect(() => {
        setCreateButtonDisabled(roomDataState?.room_permissions[0]?.only_admins_can_create_events && !isOrgAdmin)
    }, [roomDataState.id, isOrgAdmin])

    const onCreateEventClick = () => {
        if (roomDataState?.room_permissions[0]?.only_admins_can_create_events && isOrgAdmin) {
            dispatch(setModalData({ open: true, onlyAdminsCanCreateEvents: true }))
        } else {
            dispatch(setModalData({ open: true, roomCreateEventModal: {create: true} }))
        }
    }

    return (
        <div className="room-events__container">
            {role === RoleType.ORGANIZATION && (
                <TutorialBubble text={translation.tutorial_org_room_events} modifier=" speechbubble__room-events" visible={roomDataState.organization?.id === organizationDataState.id && organizationDataState.tutorial_progress === 6} hasNext={true} routeOnNext={`/organization-dashboard/${organizationDataState.id}`} />
            )}

            <div className='room-events-header'>
                <div className='room-events-header__actions'>
                    <div className='room-events-header__create-button' onClick={onCreateEventClick}>
                        <Button disabled={createButtonDisabled}
                            className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                        >
                            {translation.create_event}
                        </Button>

                    </div>
                    <div className='room-events-header__search-input'>
                        <SearchInput
                            onRemoveIconClick={() => dispatch(setRoomEventsData({ ...roomEventsState, filter: { ...roomEventsState.filter, query: '' } }))}
                            searchvalue={roomEventsState.filter.query}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                dispatch(setRoomEventsData({ ...roomEventsState, filter: { ...roomEventsState.filter, query: event.target.value } }))
                            }}
                            isGlobalSearch={false}
                            isChatSearch={false}
                        />
                    </div>
                </div>
            </div>
            <div className='room-events__content'>
                {loading ? (
                    <div className="room-events__loading">{translation.loading}</div>
                ) : (
                    <>
                        {roomEventsState.items.length > 0 ? (
                            <div className="room-events__list">
                                {roomEventsState.items.map((event: RoomEventItem) => (
                                    <RoomEventListItem key={event.id} event={event} />
                                ))}
                            </div>
                        ) : (
                            <div className="room-events__no-events">{translation.no_events}</div>
                        )}
                        {hasNextPage && (
                            <button
                                className="room-events__load-more"
                                onClick={() => setPageNum((prevPage) => prevPage + 1)}
                            >
                                {translation.load_more}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default RoomEvents